import { getChatCompletion } from './aiIntegrations/chatCompletion';

const GEMINI_PROVIDER = 'GEMINI';
const GEMINI_MODEL = 'gemini/gemini-2.5-flash';

/**
 * Compress an image File to a max dimension / quality before base64 encoding.
 * Keeps aspect ratio, outputs JPEG at 0.75 quality, max 1024px on longest side.
 * This reduces payload size significantly for large photos.
 *
 * @param {File} file - Original image file
 * @returns {Promise<string>} base64 data URI of the compressed image
 */
async function compressImageForAI(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const MAX_DIMENSION = 1024;
        const JPEG_QUALITY = 0.75;

        let { width, height } = img;

        // Scale down if either dimension exceeds MAX_DIMENSION
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width >= height) {
            height = Math.round((height / width) * MAX_DIMENSION);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width / height) * MAX_DIMENSION);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        URL.revokeObjectURL(objectUrl);
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image for compression: ${file?.name}`));
    };

    img.src = objectUrl;
  });
}

/**
 * Convert file to base64 data URI
 */
async function fileToBase64DataUri(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

/**
 * Build analysis prompt based on gender
 */
function buildAnalysisPrompt(gender) {
  const menCriteria = `
For men's photos, analyze based on these parameters (ranked by impact):

1. Body Composition and Leanness - Low body fat percentage defining facial and body proportions
2. Facial Structure and Jawline - Defined jawline signaling high testosterone, broad chin, facial symmetry
3. Expression and Vibe - Genuine smile with teeth (76% engagement boost), direct eye contact, confident "squinch"
4. Grooming and Skin Vitality - Heavy stubble (most attractive), clear skin, well-maintained hair
5. Upper Body Proportions (V-Taper) - Low Waist-to-Chest Ratio, broad shoulders, inverted triangle shape
6. Lighting and Technical Quality - Natural light, soft shadows, high resolution, eye-level or slightly above angle
7. Context and Narrative - Candid shots, hobbies/pets visible, lifestyle cues
8. Body Language and Posing - Open posture, shoulders back, relaxed contrapposto stance
9. Clothing and Style - Well-fitted modern attire, color contrast with background
10. Variety and Composition - Rule of thirds, dynamic framing, mix of close-ups and full-body
`;

  const womenCriteria = `
For women's photos, analyze based on these parameters (ranked by impact):

1. Facial Aesthetics and Neoteny - Large eyes, high cheekbones, small chin/nose, youthful features
2. Waist-to-Hip Ratio (Curviness) - Low WHR around 0.7, clear body silhouette visibility
3. Tooth-Showing Smile - Genuine Duchenne smile with teeth visible (76% more likes), eye crinkles
4. Photographic Quality and Lighting - High-resolution, natural soft lighting (golden hour), high-angle shots
5. Color Palette - Bold colors (especially red), vibrant backgrounds, strategic use of black & white
6. Candidness vs. Selfies - Candid shots preferred (15% more likes), avoid bathroom selfies (90% less engagement)
7. Grooming and Styling - "Clean girl" aesthetic, polished makeup, well-maintained hair, skin health
8. Narrative Cues - Activity shots, hobbies, travel, pets for conversation starters
`;

  return gender === 'male' ? menCriteria : womenCriteria;
}

/**
 * Analyze photos using Gemini with gender-specific criteria
 * 
 * @param {Array} files - Array of file objects with {file, name, preview, alt}
 * @param {string} gender - 'male' or 'female'
 * @returns {Promise<Array>} Ranked photos with scores and criteria breakdown
 */
export async function analyzePhotosWithGemini(files, gender = 'male') {
  try {
    const criteriaPrompt = buildAnalysisPrompt(gender);
    
    // Validate input
    if (!files || files?.length === 0) {
      throw new Error('No photos provided for analysis. Please upload at least one photo.');
    }
    
    // Convert all files to base64 (with compression to reduce payload size)
    const imagePromises = files?.map(async (fileObj) => {
      try {
        const base64DataUri = await compressImageForAI(fileObj?.file);
        return {
          base64DataUri,
          fileName: fileObj?.name,
          alt: fileObj?.alt
        };
      } catch (error) {
        throw new Error(`Failed to process image "${fileObj?.name}". The file may be corrupted or in an unsupported format.`);
      }
    });
    
    let images;
    try {
      images = await Promise.all(imagePromises);
    } catch (error) {
      throw new Error(error?.message || 'Failed to process one or more images. Please check your files and try again.');
    }
    
    // Build multimodal content array
    const content = [
      {
        type: 'text',
        text: `You are an expert photo analyst specializing in dating app profile optimization.

CRITICAL INSTRUCTION: I am sending you EXACTLY ${files?.length} photos. You MUST analyze and rank ALL ${files?.length} photos.

${criteriaPrompt}

For EACH AND EVERY ONE of the ${files?.length} photos, provide:
1. Overall attractiveness score (0-10 scale, use decimals)
2. Individual scores for the top 5 most relevant criteria from the list above
3. Brief tooltip explanation for each criterion score
4. Specific recommendations for improvement

ABSOLUTE REQUIREMENTS:
- Your response MUST include ALL ${files?.length} photos
- Each photo must have a unique rank from 1 to ${files?.length}
- The "rankedPhotos" array MUST contain EXACTLY ${files?.length} items
- Do NOT skip any photos
- Do NOT only analyze the best photo
- Return ONLY valid JSON with NO additional text, markdown, or code blocks

Return ONLY valid JSON in this exact format:
{
  "rankedPhotos": [
    {
      "originalIndex": 0,
      "rank": 1,
      "overallScore": 8.7,
      "criteria": [
        {"name": "Facial Structure", "score": 9.2, "tooltip": "Strong jawline and facial symmetry"},
        {"name": "Lighting Quality", "score": 8.9, "tooltip": "Natural light enhances features"},
        {"name": "Expression", "score": 8.5, "tooltip": "Genuine smile with good eye contact"},
        {"name": "Grooming", "score": 8.3, "tooltip": "Well-maintained appearance"},
        {"name": "Composition", "score": 8.6, "tooltip": "Good framing and background"}
      ],
      "strengths": "Excellent facial structure and natural lighting",
      "improvements": "Consider showing more of upper body for better composition"
    },
    {
      "originalIndex": 1,
      "rank": 2,
      "overallScore": 7.5,
      "criteria": [...],
      "strengths": "...",
      "improvements": "..."
    }
    // ... MUST continue for ALL ${files?.length} photos with originalIndex 0 through ${files?.length - 1}
  ],
  "summary": {
    "bestPhotoStrength": "facial structure and natural lighting",
    "bestPhotoTags": ["Professional", "Well-lit", "Confident", "Balanced"],
    "recommendations": [
      "Your top-ranked photo excels in facial symmetry and lighting",
      "Consider improving composition by following the rule of thirds",
      "Natural lighting consistently scores higher"
    ]
  }
}

REMEMBER: Analyze ALL ${files?.length} photos. Your rankedPhotos array must have ${files?.length} items.`
      }
    ];
    
    // Add each image to content array
    images?.forEach((img) => {
      content?.push({
        type: 'image_url',
        image_url: {
          url: img?.base64DataUri
        }
      });
    });
    
    // Call Gemini via Lambda with timeout handling
    let response;
    const requestStartTime = Date.now();
    const TIMEOUT_MS = 60000; // 60 second timeout
    
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timeout: Analysis took too long to complete. Try reducing the number of photos or compressing large images.'));
        }, TIMEOUT_MS);
      });

      // Race between API call and timeout
      response = await Promise.race([
        getChatCompletion(
          GEMINI_PROVIDER,
          GEMINI_MODEL,
          [
            {
              role: 'user',
              content
            }
          ],
          {
            temperature: 0.3,
            max_tokens: 8192
          }
        ),
        timeoutPromise
      ]);
      
    } catch (error) {
      console.error('❌ Gemini API call failed:', error);
      const requestDuration = Date.now() - requestStartTime;
      
      // Network errors
      if (error?.message?.includes('network') || error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        throw new Error('Network error: Unable to reach the AI service. Please check your internet connection and try again.');
      }
      
      // Timeout errors
      if (error?.message?.includes('timeout') || error?.message?.includes('timed out') || requestDuration >= TIMEOUT_MS) {
        throw new Error('Request timeout: Analysis took too long to complete. Try reducing the number of photos or using a faster internet connection.');
      }
      
      // Authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('unauthorized')) {
        throw new Error('Authentication error: Invalid API credentials. Please refresh the page or contact support.');
      }
      
      // Rate limit errors
      if (error?.message?.includes('429') || error?.message?.includes('rate limit') || error?.message?.includes('too many requests')) {
        throw new Error('Rate limit exceeded: Too many requests. Please wait 60 seconds and try again.');
      }
      
      // Server errors
      if (error?.message?.includes('500') || error?.message?.includes('503') || error?.message?.includes('502') || error?.message?.includes('504')) {
        throw new Error('AI service temporarily unavailable: The service is experiencing issues. Please try again in a few moments.');
      }
      
      // Generic error with original message
      throw new Error('Failed to analyze photos: ' + (error?.message || 'Unknown error occurred. Please try again.'));
    }
    
    // Extract and parse JSON response
    const finishReason = response?.choices?.[0]?.finish_reason;
    if (finishReason === 'content_filter' || finishReason === 'SAFETY') {
      throw new Error('One or more photos was blocked by the AI safety filter. Please try different photos.');
    }

    const aiResponse = response?.choices?.[0]?.message?.content || '';
    
    if (!aiResponse) {
      throw new Error('AI service returned an empty response. Please try again.');
    }
    
    // Try to extract JSON from response (handle markdown code blocks)
    let jsonStr = aiResponse;
    const jsonMatch = aiResponse?.match(/```json\s*([\s\S]*?)\s*```/) || aiResponse?.match(/```\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      jsonStr = jsonMatch?.[1];
    }
    
    let analysisResult;
    try {
      analysisResult = JSON.parse(jsonStr);
    } catch (parseError) {
      console.error('Failed to parse AI response:', parseError);
      throw new Error('AI returned an invalid response format. Please try again or contact support if the issue persists.');
    }
    
    // Validate that we got all photos
    if (!analysisResult?.rankedPhotos || analysisResult?.rankedPhotos?.length === 0) {
      throw new Error('AI did not return any photo rankings. Please try again.');
    }
    
    if (analysisResult?.rankedPhotos?.length !== files?.length) {
      console.error(`Photo count mismatch: Expected ${files?.length}, got ${analysisResult?.rankedPhotos?.length}`);
      throw new Error(`AI only analyzed ${analysisResult?.rankedPhotos?.length} out of ${files?.length} photos. Please try again with a clearer set of photos.`);
    }
    
    // Validate all originalIndex values are present
    const receivedIndices = analysisResult?.rankedPhotos?.map(p => p?.originalIndex)?.sort();
    const expectedIndices = Array.from({ length: files?.length }, (_, i) => i);
    const missingIndices = expectedIndices?.filter(i => !receivedIndices?.includes(i));
    
    if (missingIndices?.length > 0) {
      console.error(`Missing photo indices:`, missingIndices);
      throw new Error(`AI skipped photos at positions: ${missingIndices?.map(i => i + 1)?.join(', ')}. Please try again.`);
    }

    // Validate no duplicate originalIndex values (AI may return same index twice)
    const indexCounts = receivedIndices?.reduce((acc, idx) => {
      acc[idx] = (acc?.[idx] || 0) + 1;
      return acc;
    }, {});
    const duplicateIndices = Object.entries(indexCounts)
      ?.filter(([, count]) => count > 1)
      ?.map(([idx]) => Number(idx));

    if (duplicateIndices?.length > 0) {
      console.warn(`Duplicate originalIndex values detected: ${duplicateIndices}. Attempting auto-repair.`);

      // Auto-repair: assign missing indices to duplicate entries
      const usedIndices = new Set();
      const repairedPhotos = [];
      const availableIndices = [...expectedIndices];

      for (const photo of analysisResult?.rankedPhotos) {
        if (!usedIndices?.has(photo?.originalIndex)) {
          usedIndices?.add(photo?.originalIndex);
          repairedPhotos?.push(photo);
        } else {
          // Find the next unused index
          const nextIndex = availableIndices?.find(i => !usedIndices?.has(i));
          if (nextIndex !== undefined) {
            usedIndices?.add(nextIndex);
            repairedPhotos?.push({ ...photo, originalIndex: nextIndex });
          }
        }
      }

      analysisResult.rankedPhotos = repairedPhotos;
    }
    
    // Map results back to original files with images
    const rankedPhotos = analysisResult?.rankedPhotos?.map((photo) => {
      const originalFile = files?.[photo?.originalIndex];
      if (!originalFile) {
        throw new Error(`Internal error: Could not find uploaded file for photo ${photo?.originalIndex + 1}`);
      }
      return {
        id: `photo-${photo?.originalIndex}`,
        rank: photo?.rank,
        image: originalFile?.preview,
        imageAlt: originalFile?.alt,
        overallScore: photo?.overallScore,
        isFavorite: photo?.rank === 1,
        criteria: photo?.criteria,
        strengths: photo?.strengths,
        improvements: photo?.improvements
      };
    });
    
    return {
      rankedPhotos,
      summary: analysisResult?.summary
    };
    
  } catch (error) {
    console.error('❌ Photo analysis error:', error);
    throw new Error(error?.message || 'Failed to analyze photos. Please try again.');
  }
}

/**
 * Generate summary data from ranked photos
 */
export function generateSummaryData(rankedPhotos, summaryFromAI) {
  const scores = rankedPhotos?.map(p => p?.overallScore);
  const totalPhotos = rankedPhotos?.length;
  const averageScore = scores?.reduce((sum, score) => sum + score, 0) / totalPhotos;
  const highestScore = Math.max(...scores);
  const lowestScore = Math.min(...scores);
  const scoreRange = highestScore - lowestScore;
  
  return {
    totalPhotos,
    averageScore,
    highestScore,
    scoreRange,
    analyzedDate: new Date()?.toISOString(),
    recommendations: summaryFromAI?.recommendations || [
      'Your top-ranked photo excels in key attractiveness criteria',
      'Consider the feedback provided for each photo to improve future shots',
      'Natural lighting and genuine expressions consistently score higher'
    ],
    bestPhotoRank: 1,
    bestPhotoStrength: summaryFromAI?.bestPhotoStrength || 'overall composition and expression',
    bestPhotoTags: summaryFromAI?.bestPhotoTags || ['Top Rated', 'Attractive', 'Well-Composed']
  };
}